import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Package, Search, Filter, ShoppingCart, FolderOpen, RefreshCw, Globe, Database, ShieldCheck, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '../../lib/skeletons';
import { fmtFecha } from '../../lib/utils';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

export default function ItemsOC() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ total: 0, ots_afectadas: 0, por_familia: {} });
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState('');
  const searchTimer = useRef(null);
  const [debouncedQ, setDebouncedQ] = useState('');
  const [filterFamilia, setFilterFamilia] = useState('');
  const [filterOt, setFilterOt] = useState('');
  const [filterOrigen, setFilterOrigen] = useState(''); // '' | 'web' | 'access'
  const [soloAprobadas, setSoloAprobadas] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 200;

  const loadData = useCallback(async (currentPage = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.append('q', debouncedQ);
      if (filterFamilia) params.append('familia', filterFamilia);
      if (filterOt) params.append('ot', filterOt);
      if (filterOrigen) params.append('source', filterOrigen);
      if (soloAprobadas) params.append('solo_aprobadas', 'true');
      params.append('skip', String(currentPage * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      const res = await api.get(`/compras/items-oc?${params}`);
      setData(res.data.items || []);
      setStats({ total: res.data.total, ots_afectadas: res.data.ots_afectadas, por_familia: res.data.por_familia || {} });
    } catch {
      toast.error('Error cargando items de OCs');
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, filterFamilia, filterOt, filterOrigen, soloAprobadas]);

  useEffect(() => { setPage(0); loadData(0); }, [loadData]);

  const handleSearch = (val) => {
    setQ(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedQ(val), 400);
  };

  const familias = useMemo(
    () => Object.entries(stats.por_familia).sort((a, b) => b[1] - a[1]),
    [stats.por_familia]
  );

  const handleVerOC = (numeroOc) => {
    navigate(`/compras/ordenes-compra?open=${encodeURIComponent(numeroOc)}`);
  };

  return (
    <div className="p-4 space-y-3" data-testid="items-oc-page">
      {/* Header */}
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            Items de Órdenes de Compra
          </h1>
          <p className="text-sm text-muted-foreground">
            Listado plano de líneas de OC. Filtrá por familia, OT, origen, aprobación o texto.
          </p>
        </div>
        <Button onClick={() => loadData(page)} variant="outline" size="sm" data-testid="items-oc-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-total"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100"><Package className="w-5 h-5 text-indigo-600" /></div>
          <div>
            <p className="text-[11px] text-muted-foreground leading-tight">Items</p>
            <p className="text-xl font-bold">{fmt(stats.total)}</p>
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-ots"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100"><ShoppingCart className="w-5 h-5 text-blue-600" /></div>
          <div>
            <p className="text-[11px] text-muted-foreground leading-tight">OTs afectadas</p>
            <p className="text-xl font-bold">{stats.ots_afectadas}</p>
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-familias"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100"><FolderOpen className="w-5 h-5 text-amber-600" /></div>
          <div>
            <p className="text-[11px] text-muted-foreground leading-tight">Familias</p>
            <p className="text-xl font-bold">{familias.length}</p>
          </div>
        </CardContent></Card>
        <Card data-testid="kpi-origen"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-100">
            {filterOrigen === 'web' ? <Globe className="w-5 h-5 text-indigo-600" /> :
             filterOrigen === 'access' ? <Database className="w-5 h-5 text-slate-600" /> :
             <ShieldCheck className="w-5 h-5 text-green-600" />}
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground leading-tight">Origen</p>
            <p className="text-sm font-bold capitalize">{filterOrigen || 'Todas'}</p>
          </div>
        </CardContent></Card>
      </div>

      {/* Familias chips - vista por tipos */}
      {familias.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="familias-chips">
          <span className="text-[11px] text-muted-foreground mr-1">Vista por familia:</span>
          <button
            onClick={() => setFilterFamilia('')}
            data-testid="familia-chip-todas"
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${filterFamilia === '' ? 'bg-gray-800 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            Todas ({stats.total})
          </button>
          {familias.slice(0, 12).map(([f, c]) => (
            <button
              key={f}
              onClick={() => setFilterFamilia(f === 'Sin Familia' ? '' : f)}
              data-testid={`familia-chip-${f.replace(/\s+/g, '-').toLowerCase()}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${filterFamilia === f ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
              title={f}
            >
              {f.length > 30 ? f.slice(0, 30) + '…' : f} ({c})
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
          <Filter className="w-3.5 h-3.5" /> Filtros:
        </div>
        <div className="flex items-center gap-1 text-xs border rounded-md overflow-hidden h-8" data-testid="items-oc-origen">
          <span className="px-2 text-gray-500 bg-gray-50 h-full flex items-center">Origen:</span>
          <button onClick={() => setFilterOrigen('')} data-testid="items-oc-origen-todas"
            className={`px-2.5 h-full transition-colors ${filterOrigen === '' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Todas</button>
          <button onClick={() => setFilterOrigen('web')} data-testid="items-oc-origen-web"
            className={`px-2.5 h-full transition-colors ${filterOrigen === 'web' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Web</button>
          <button onClick={() => setFilterOrigen('access')} data-testid="items-oc-origen-access"
            className={`px-2.5 h-full transition-colors ${filterOrigen === 'access' ? 'bg-slate-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Access</button>
        </div>
        <Input placeholder="OT..." value={filterOt} onChange={(e) => setFilterOt(e.target.value)}
          className="h-8 w-20 text-xs" data-testid="items-oc-filter-ot" />
        <select value={filterFamilia} onChange={(e) => setFilterFamilia(e.target.value)}
          className="h-8 px-2 rounded-md border border-input text-xs max-w-[220px]" data-testid="items-oc-filter-familia">
          <option value="">Todas las Familias</option>
          {familias.map(([f, c]) => <option key={f} value={f}>{f} ({c})</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input placeholder="Buscar descripción, OC, proveedor..." value={q}
            onChange={(e) => handleSearch(e.target.value)}
            className="h-8 pl-7 text-xs" data-testid="items-oc-search" />
        </div>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={soloAprobadas} onChange={(e) => setSoloAprobadas(e.target.checked)} data-testid="items-oc-solo-aprobadas" />
          Solo aprobadas
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <TableSkeleton rows={10} cols={9} />
      ) : data.length === 0 ? (
        <div className="text-center py-10 text-sm text-muted-foreground border rounded-lg">
          No hay items que coincidan con los filtros.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs" data-testid="items-oc-table">
              <thead className="bg-gray-50 border-b">
                <tr className="text-[10px] uppercase text-muted-foreground text-left">
                  <th className="p-2">OC</th>
                  <th className="p-2">Origen</th>
                  <th className="p-2">Estado</th>
                  <th className="p-2">Fecha</th>
                  <th className="p-2">OT</th>
                  <th className="p-2">Descripción</th>
                  <th className="p-2">Familia</th>
                  <th className="p-2 text-right">Cant</th>
                  <th className="p-2">UM</th>
                  <th className="p-2 text-right">$ Unit</th>
                  <th className="p-2 text-right">$ Total</th>
                  <th className="p-2">Proveedor</th>
                  <th className="p-2 text-center">Ver</th>
                </tr>
              </thead>
              <tbody>
                {data.map((it, idx) => (
                  <tr key={`${it.numero_oc}-${it.item}-${idx}`} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-mono font-bold text-indigo-700">{it.numero_oc}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${it.source === 'web' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                        {it.source === 'web' ? 'WEB' : 'ACC'}
                      </span>
                    </td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${it.aprobada ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                        {it.aprobada ? 'Aprobada' : 'Sin VB'}
                      </span>
                    </td>
                    <td className="p-2">{fmtFecha(it.fecha)}</td>
                    <td className="p-2 font-mono">{it.ot_numero || '-'}</td>
                    <td className="p-2 max-w-[300px] truncate" title={it.descripcion}>{it.descripcion}</td>
                    <td className="p-2 text-muted-foreground truncate max-w-[150px]" title={it.concepto}>{it.concepto || '-'}</td>
                    <td className="p-2 text-right font-medium">{fmt(it.cantidad)}</td>
                    <td className="p-2">{it.unidad}</td>
                    <td className="p-2 text-right font-mono">${fmt(it.valor_unitario)}</td>
                    <td className="p-2 text-right font-mono font-bold">${fmt(it.total_linea)}</td>
                    <td className="p-2 truncate max-w-[180px]" title={it.proveedor_nombre}>{it.proveedor_nombre || '-'}</td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => handleVerOC(it.numero_oc)}
                        className="text-indigo-600 hover:text-indigo-800"
                        data-testid={`items-oc-ver-${it.numero_oc}`}
                        title="Ver OC"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {stats.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>
            Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, stats.total)} de {fmt(stats.total)} items
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0}
              onClick={() => { const p = page - 1; setPage(p); loadData(p); }}
              data-testid="items-oc-prev">Anterior</Button>
            <span>Pág {page + 1} / {Math.ceil(stats.total / PAGE_SIZE)}</span>
            <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= stats.total}
              onClick={() => { const p = page + 1; setPage(p); loadData(p); }}
              data-testid="items-oc-next">Siguiente</Button>
          </div>
        </div>
      )}
    </div>
  );
}
